import React, { useEffect, useRef, useState } from 'react';
import { Flex, ProgressCircle, ToastQueue } from '@adobe/react-spectrum';
import { GetContactListRequest } from '../../../../../services/soap/main/requests/GetContactListRequest';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { useTranslation } from 'react-i18next';
import { useAsyncList } from 'react-stately';
import Select, { components } from 'react-select';
import { useDebounce } from '@uidotdev/usehooks';
import { truncateText } from '../../../../../utils/helperUtil';
import styles from './ContactsFilterMultipleTag.module.css';

interface ContactsFilterComponentProps {
  params: any;
  createParams: (filterType: any, filterValue: any) => void;
  fieldName: string;
  setContactsSelected?: any[];
}

interface Contact {
  id: string;
  name: string;
  isEditing?: boolean; // Add a flag for editing state
  tempName?: string; // Add a temporary name for editing
}

const ContactsFilterMultipleTag: React.FC<ContactsFilterComponentProps> = ({ createParams, fieldName, params, setContactsSelected }) => {
  const { store, mainService } = useDependency();
  const { t } = useTranslation();
  const [selectedContacts, setSelectedContacts] = useState<any[]>(setContactsSelected || []);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const refSelectedContacts = useRef<any[]>([]);
  const debouncedInputValue = useDebounce(inputValue, 1000);

  const contactsList = useAsyncList({
    async load({ filterText }) {
      try {
        //setShowLoader(true);
        const content = filterText ?? '';

        let contactsResp = await mainService.getContactList(
          new GetContactListRequest(store.Server, store.SessionId, {
            offset: 0,
            limit: 20,
            contains: '*' + content + '*',
            ...params,
          })
        );

        if (contactsResp.result === 'OK' && contactsResp?.CONTACT) {
          const formattedArray = contactsResp.CONTACT.map((row: any) => {
            //console.log('object', row);
            // For "onlyUser: 1" results, server provides the real login in `sysUserName`
            // (e.g. "grunt@pioneer"). Some contacts have `email: "Email"` which is not usable.
            const derivedUserName =
              row?.sysUserName ??
              row?.userName ??
              row?.username ??
              row?.login ??
              row?.mail ??
              row?.mailAddress ??
              row?.emailText ??
              row?.email ??
              undefined;
            const mapped = {
              id: String(row.id),
              // For NewMail notify we need system user id/login, not the contact id.
              sysUserId: row?.sysUser != null ? String(row.sysUser).trim() : undefined,
              contactName: `${row?.name3 !== undefined ? row?.name3 : ''} ${row?.name1}`,
              userName: derivedUserName ? String(derivedUserName).trim() : undefined,
              value: String(row.id),
              label: `${row?.name3 !== undefined ? row?.name3 : ''} ${row?.name1}`,
            };
            return mapped;
          });
          return {
            items: formattedArray,
          };
        } else {
          return { items: [] };
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        ToastQueue.negative(`Error fetching contacts: ${error}`, { timeout: 3000 });
        return { items: [] };
      } finally {
        setShowLoader(false);
      }
    },
  });

  useEffect(() => {
    setSelectedContacts(setContactsSelected || []);
  }, [setContactsSelected]);

  useEffect(() => {
    // Update the filter text in contactsList when the debounced input value changes
    contactsList.setFilterText(debouncedInputValue);
  }, [debouncedInputValue]);

  // Handle selection changes from react-select
  const handleChange = (newValue: any, actionMeta: any) => {
    //console.log('newValue', newValue);
    // Convert react-select format to our internal format
    const newContacts = newValue.map((item: any) => ({
      id: item.value,
      value: item.value,
      contactName: item.label,
      userName: item.userName,
      sysUserId: item.sysUserId,
      label: item.label,
    }));

    refSelectedContacts.current = newContacts;
    setSelectedContacts(newContacts);

    // Update parent component with selected values
    const updateValue =
      newContacts.length > 0
        ? new Set(
            newContacts.map((item: any) => ({
              id: item.id,
              value: item.value,
              contactName: item.label,
              userName: item.userName,
              sysUserId: item.sysUserId,
              label: item.label,
            }))
          )
        : '';

    createParams(fieldName, updateValue);
  };

  // Custom styles for react-select
  const customStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '10px',
      fontSize: '12px',
      border: 'none',
      height: '40px',
      top: '-5px',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: 'white',
      borderRadius: '4px',
      margin: '2px',
      padding: '0',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      fontSize: '12px',
      //color: 'var(--spectrum-global-color-gray-800)',
      //padding: '2px 6px',
      backgroundColor: 'white',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      //display: 'none',
      backgroundColor: '#d8dee4',
      borderRadius: '50%',
      color: '#fff',
      padding: '0px',
      marginLeft: '-25px',
      marginTop: '8px',
      height: '18px',
      ':hover': {
        //backgroundColor: '#E2E8F0',
        // color: '#E53E3E',
      },
    }),
  };

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  }

  const updateParentComponent = (contacts: Contact[]) => {
    const updateValue =
      contacts.length > 0
        ? new Set(
            contacts.map((item: any) => ({
              id: item.id,
              value: item.id,
              contactName: item.contactName,
              userName: (item as any).userName,
              sysUserId: (item as any).sysUserId,
              label: item.label,
            }))
          )
        : '';

    createParams(fieldName, updateValue);
  };

  // Convert contactsList.items to react-select format
  const options = contactsList.items.map((item: any) => ({
    value: item.id,
    label: item.contactName,
    userName: item.userName,
    sysUserId: item.sysUserId,
  }));

  // Convert selectedContacts to react-select format
  const selectedValues = selectedContacts.map(contact => ({
    value: contact.id ?? contact.value,
    label: contact.contactName ?? contact.label,
    userName: (contact as any).userName,
    sysUserId: (contact as any).sysUserId,
  }));

  const handleEditClick = (contact: Contact) => {
    setSelectedContacts(selectedContacts.map(c => (c.id === contact.id ? { ...c, isEditing: true, tempName: c.contactName } : c)));
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, contact: Contact) => {
    e.stopPropagation(); // Important - prevent react-select from capturing these events
    if (e.key === 'Enter') {
      handleEditSave(contact);
    } else if (e.key === 'Escape') {
      handleEditCancel(contact);
    }
  };

  const handleEditChange = (e: any, contact: Contact) => {
    setSelectedContacts(selectedContacts.map(c => (c.id === contact.id ? { ...c, tempName: e.target.value } : c)));
  };

  const handleEditSave = (contact: Contact) => {
    setSelectedContacts(selectedContacts.map(c => (c.id === contact.id ? { ...c, name: contact.tempName!, isEditing: false } : c)));
    refSelectedContacts.current = selectedContacts.map(c => (c.id === contact.id ? { ...c, name: contact.tempName!, isEditing: false } : c));
    updateParentComponent(refSelectedContacts.current);
  };

  const handleEditCancel = (contact: Contact) => {
    setSelectedContacts(selectedContacts.map(c => (c.id === contact.id ? { ...c, isEditing: false, tempName: undefined } : c)));
  };

  return (
    <Flex direction="column" alignSelf="stretch" width="100%">
      <Flex direction="column" gap="size-100" alignSelf="stretch" width="100%" UNSAFE_style={{ fontFamily: '"Adobe Clean", sans-serif', borderRadius: '4px', overflowX: 'auto' }}>
        <Select
          isMulti
          isClearable={false}
          isSearchable
          name="contacts"
          options={options}
          value={selectedValues}
          inputValue={inputValue}
          onInputChange={value => {
            setInputValue(value);
          }}
          onChange={handleChange}
          placeholder={t('search_contacts', { ns: 'layout_components' })}
          noOptionsMessage={({ inputValue }) => (!inputValue ? t('type_to_search_contacts', { ns: 'layout_components' }) : t('no_matching_contacts_found', { ns: 'layout_components' }))}
          isLoading={contactsList.loadingState === 'loading'}
          styles={{
            ...customStyles,
            // Add these to prevent focus issues
            multiValue: (base: any) => ({
              ...base,
              backgroundColor: 'white',
              borderRadius: '4px',
              //padding: '0 0 0 4px', // Add padding for edit button
            }),
            // Ensure input container doesn't interfere
            valueContainer: (base: any) => ({
              ...base,
              //padding: '2px 8px',
            }),
          }}
          menuPosition="fixed"
          components={{
            // Override the MultiValue component to handle editing
            MultiValue: (props: any) => {
              const contact = selectedContacts.find(c => c.id === props.data.value);
              //console.log('contact', contact);
              // If this tag is being edited
              if (contact?.isEditing) {
                return (
                  <components.MultiValue {...props}>
                    <Flex alignItems={'center'}>
                      <input
                        type="text"
                        value={contact.tempName}
                        onChange={e => handleEditChange(e, contact)}
                        onKeyDown={e => handleEditKeyDown(e, contact)}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        style={{
                          fontSize: '12px',
                          marginRight: '4px',
                          border: '1px solid #ccc',
                          padding: '2px',
                          width: Math.max(50, (contact.tempName?.length || 0) * 7) + 'px',
                          outline: 'none',
                        }}
                        autoFocus
                      />
                      {/* <button
                          onClick={e => {
                            e.stopPropagation();
                            handleEditSave(contact);
                          }}
                          style={{ marginRight: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          <i className="bi bi-check-circle-fill" style={{ color: 'green' }}></i>
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleEditCancel(contact);
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          <i className="bi bi-x-circle-fill" style={{ color: 'red' }}></i>
                        </button> */}
                    </Flex>
                  </components.MultiValue>
                );
              }

              // Regular non-editing tag
              return (
                <components.MultiValue {...props}>
                  <Flex alignItems={'center'}>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleEditClick(contact);
                      }}
                      className={styles.tag}
                    >
                      {/* <i className="bi bi-pencil-square"></i> */} {truncateText(props.children, 40)}
                    </button>
                    {/* {props.children} */}
                  </Flex>
                </components.MultiValue>
              );
            },
          }}
        />
      </Flex>
    </Flex>
  );
};

export default ContactsFilterMultipleTag;
